import { useState } from 'preact/hooks';
import { requestMagicLink, submitAccessRequest } from '../auth.js';
import { Banner } from '../components/Banner.js';

/** Request-access (waitlist) form shown when the signup gate blocks the email. */
function RequestAccess(props: { email: string; onBack: () => void }): preact.ComponentChildren {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [useCase, setUseCase] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const input: { email: string; name?: string; company?: string; useCase?: string } = {
        email: props.email,
      };
      if (name.trim()) input.name = name.trim();
      if (company.trim()) input.company = company.trim();
      if (useCase.trim()) input.useCase = useCase.trim();
      await submitAccessRequest(input);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div class="sent">
        <h2>Request received</h2>
        <p>
          We review every request and will email <strong>{props.email}</strong> as soon as your
          access opens. Meanwhile, the{' '}
          <a href="https://wholisphere.ai/#demo" target="_blank" rel="noreferrer noopener">
            live demo
          </a>{' '}
          and the browser extension work without an account.
        </p>
        <button type="button" class="secondary" onClick={props.onBack}>
          Back to sign in
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={submit}>
      <h2>Request access</h2>
      <p class="lead">
        We're invite-only while in beta. Tell us a little about you and we'll email{' '}
        <strong>{props.email}</strong> when a spot opens — usually within a day or two.
      </p>
      <div class="field">
        <label htmlFor="ar-name">Name (optional)</label>
        <input
          id="ar-name"
          type="text"
          autoComplete="name"
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <label htmlFor="ar-company">Company (optional)</label>
        <input
          id="ar-company"
          type="text"
          autoComplete="organization"
          value={company}
          onInput={(e) => setCompany((e.target as HTMLInputElement).value)}
        />
      </div>
      <div class="field">
        <label htmlFor="ar-usecase">What do you want to use Wholisphere for? (optional)</label>
        <input
          id="ar-usecase"
          type="text"
          placeholder="e.g. VPAT for our customer portal"
          value={useCase}
          onInput={(e) => setUseCase((e.target as HTMLInputElement).value)}
        />
      </div>
      {error && <Banner variant="warn">{error}</Banner>}
      <button type="submit" disabled={busy}>
        {busy ? 'Sending…' : 'Request access'}
      </button>
      <button
        type="button"
        class="secondary"
        style={{ marginTop: 8 }}
        onClick={props.onBack}
        disabled={busy}
      >
        Back
      </button>
    </form>
  );
}

export function Login(): preact.ComponentChildren {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<{ devLink?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [needsAccess, setNeedsAccess] = useState(false);

  const submit = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await requestMagicLink(email);
      const next: { devLink?: string } = {};
      if (result.devLink !== undefined) next.devLink = result.devLink;
      setSent(next);
    } catch (e) {
      const code = (e as Error & { code?: string }).code;
      if (code === 'signup_not_allowed') {
        // Invite-only gate — offer the waitlist instead of a raw error.
        setNeedsAccess(true);
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div class="login">
      <div class="login-card">
        <div class="brand">
          <img class="logo" src="/logo.jpg" alt="" width="24" height="24" />
          <span>Wholisphere</span>
        </div>
        {needsAccess ? (
          <RequestAccess email={email} onBack={() => setNeedsAccess(false)} />
        ) : (
          <>
            <h1>Sign in</h1>
            <p class="lead">
              Enter your email and we'll send a one-time link to sign in. No password needed.
            </p>
            {window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1' ? (
              <div
                style={{
                  padding: '10px 12px',
                  marginBottom: 16,
                  fontSize: 12,
                  border: '1px solid #334',
                  borderRadius: 8,
                  background: 'rgba(96,165,250,0.08)',
                  color: 'var(--fg-muted, #9aa4b2)',
                }}
              >
                <strong>Local dev</strong> · any email works. The confirmation link will appear
                below the form on submit (no real inbox needed).
              </div>
            ) : null}
            {sent ? (
              <div class="sent">
                <h2>Check your email</h2>
                <p>
                  If the address exists, a sign-in link is on its way to <strong>{email}</strong>.
                  The link expires in 15 minutes.
                </p>
                {sent.devLink && (
                  <div class="dev-link">
                    <p class="muted">
                      <strong>Dev mode:</strong> Resend isn't configured, so here's your link:
                    </p>
                    <a href={sent.devLink}>{sent.devLink}</a>
                  </div>
                )}
                <button type="button" class="secondary" onClick={() => setSent(null)}>
                  Use a different email
                </button>
              </div>
            ) : (
              <form onSubmit={submit}>
                <div class="field">
                  <label htmlFor="email">Work email</label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    placeholder="you@yourcompany.com"
                    value={email}
                    onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
                  />
                </div>
                {error && <Banner variant="warn">{error}</Banner>}
                <button type="submit" disabled={busy || email.length < 3}>
                  {busy ? 'Sending…' : 'Send magic link'}
                </button>
                <a
                  class="btn-link secondary"
                  style={{ display: 'inline-block', marginTop: 8 }}
                  href="/v1/auth/sso/google/start"
                >
                  Sign in with Google Workspace
                </a>
                <p class="muted small">
                  By continuing you agree to the{' '}
                  <a href="https://wholisphere.ai/terms" target="_blank" rel="noreferrer noopener">
                    terms
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://wholisphere.ai/privacy"
                    target="_blank"
                    rel="noreferrer noopener"
                  >
                    privacy policy
                  </a>
                  .
                </p>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
